import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, ScrollView, StyleSheet, View } from 'react-native';
import {
  useTheme,
  DETAIL_PANEL_WIDTH,
  DOCK_PANEL_TRANSITION_MS,
  DOCK_TAB_WIDTH,
  Z_INDEX,
  ELEVATION,
  durations,
} from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { PanelShownContext } from '../../utils/panelShown';
import { DockTab } from './DockTab';

interface DockedPanelProps {
  children: React.ReactNode;
  testID?: string;
  /** Panel width; defaults to the structure detail width. */
  width?: number;
  /** Which edge the panel docks to and slides in from. Defaults to 'right'
   *  (structure detail, observation presentation). 'left' is the defense
   *  plan editor/viewer (Sept 2026 — "come out from the left side and cover
   *  up" the sidebar, giving the map the sidebar's width back). */
  side?: 'left' | 'right';
  /** Wrap children in a ScrollView (the default). Pass false when the
   *  child lays out its own fixed header/footer around an inner scroller,
   *  as the defense plan editor does: inside this ScrollView the inner one
   *  never gets a bounded height, so it grows to its content and the whole
   *  panel scrolls instead, footer and all (Sept 24 2026: at 1280x720 the
   *  editor's "Download needs list" sat below the window). */
  scroll?: boolean;
  /** Distance from the edge this panel starts at, in px (panel-system.md
   *  phase 0). A companion panel passes its parent's width here so it
   *  stacks beside it instead of overlapping it. Defaults to 0, so every
   *  existing caller docks flush to the edge exactly as before. */
  offset?: number;
  /** Render the DOCK_TAB_WIDTH collapsed tab instead of the panel body.
   *  Children stay mounted (display:'none') so their form state survives
   *  a collapse/expand round trip — see resolvePanelLayout for who decides
   *  this. */
  collapsed?: boolean;
  /** Label the collapsed tab reads top-to-bottom. Required in practice
   *  whenever `collapsed` can be true; DockTab falls back to an empty
   *  string otherwise. */
  collapsedLabel?: string;
  /** Called after the tab press sets this side as newest — the caller's
   *  own re-expand logic (e.g. clearing whatever collapsed it). */
  onExpand?: () => void;
  /** Controls an exit animation instead of an instant unmount. Defaults to
   *  true, matching every existing caller (mount is open, unmount is
   *  closed, no exit to animate). Set to false to slide the panel back out
   *  over `durations.quick`; the panel stays mounted throughout — never
   *  unmount mid-animation — and `onExited` fires once it's off-screen, so
   *  the caller can unmount then. */
  shown?: boolean;
  /** Fires once the exit animation finishes (or immediately, under reduced
   *  motion). Only meaningful alongside `shown={false}`. */
  onExited?: () => void;
  /** Element id (nativeID) of this panel's own heading, wired to
   *  aria-labelledby. Panels are regions, never dialogs — see CLAUDE.md's
   *  "Panels, not dialogs" rule. Omit both this and `accessibilityLabel`
   *  and no region role is added at all, so a caller that doesn't opt in
   *  renders the exact DOM it did before this prop existed. */
  ariaLabelledBy?: string;
  /** Plain-string alternative to ariaLabelledBy when there's no on-screen
   *  heading element to point at. */
  accessibilityLabel?: string;
  /** Token for the panel's fill. Defaults to `surfaceElevated` (every
   *  existing dock). A companion uses `surfaceAlt` so it reads as
   *  secondary to its parent in both themes (panel-system.md, "Dark
   *  theme"). */
  backgroundColor?: 'surfaceElevated' | 'surfaceAlt';
  /** How far the panel travels when it enters or leaves, in px. Defaults to
   *  its own width (a full slide from the screen edge). A companion passes a
   *  short distance so it emerges from beside its parent instead of sweeping
   *  across it. */
  slideDistance?: number;
  /** Fade the panel with the slide (opacity follows the slide's progress),
   *  so a short slide over a neighbor doesn't paint the panel on top of it
   *  at full strength. */
  fade?: boolean;
  /** Web only: animate `offset` and width changes over `durations.base`
   *  instead of snapping (a companion folding into or out of its parent).
   *  JS-driven layout animation, so it is skipped on native, where this
   *  component's transform animation runs on the native driver and the two
   *  can't share a view. */
  animateLayout?: boolean;
}

// Slide-in on open. Short enough that the panel is settled before anyone
// reaches for it, long enough to show where it came from. Shared with
// webStyles.TRANSITION_DOCK_INSET so map chrome shifting to clear this panel
// moves at the same speed.
const OPEN_MS = DOCK_PANEL_TRANSITION_MS;

// Matches theme/motion.ts's `easings.exit` (cubic-bezier(0.4, 0, 1, 1)) — RN
// call sites keep their own Easing curve rather than parsing the CSS string
// (see motion.ts's file comment).
const EXIT_EASING = Easing.bezier(0.4, 0, 1, 1);

// Registry key when the caller passes no testID (the structure detail
// panel's case, the only DockedPanel usage without one).
const DEFAULT_ID = 'docked-panel';

/**
 * Desktop-shell presentation surface: a right-docked panel that OVERLAYS its
 * parent (the map) instead of resizing it — Leaflet never sees a container
 * resize on open/close. Mounted only while open, so closing leaves nothing
 * behind (the web inline-sheet lesson). Content scrolls internally.
 *
 * Because it is mounted only while open, mount is open: the panel starts one
 * width to the right and slides home. `useNativeDriver` follows the house
 * rule (useAnimations.ts): off on web, on everywhere else.
 *
 * `shown` (default true) adds an exit path for callers that want to animate
 * a close instead of unmounting outright — see its doc comment. Every
 * caller that doesn't pass it keeps the exact behavior above.
 */
export function DockedPanel({
  children,
  testID,
  width = DETAIL_PANEL_WIDTH,
  side = 'right',
  scroll = true,
  offset = 0,
  collapsed = false,
  collapsedLabel,
  onExpand,
  shown = true,
  onExited,
  ariaLabelledBy,
  accessibilityLabel,
  backgroundColor = 'surfaceElevated',
  slideDistance,
  fade = false,
  animateLayout = false,
}: DockedPanelProps) {
  const { colors } = useTheme();
  const reducedMotion = useReducedMotion();
  // Right docks slide in from the right (start translateX +width); left
  // docks slide in from the left (start translateX -width). Either way the
  // panel settles at translateX 0.
  const distance = slideDistance ?? width;
  const slideIn = useRef(new Animated.Value(side === 'left' ? -distance : distance)).current;
  const layoutAnimated = animateLayout && Platform.OS === 'web';
  const shownWidth = collapsed ? DOCK_TAB_WIDTH : width;
  const offsetAnim = useRef(new Animated.Value(offset)).current;
  const widthAnim = useRef(new Animated.Value(shownWidth)).current;
  useEffect(() => {
    if (!layoutAnimated) return;
    const animation = Animated.parallel(
      [
        [offsetAnim, offset],
        [widthAnim, shownWidth],
      ].map(([value, toValue]) =>
        Animated.timing(value as Animated.Value, {
          toValue: toValue as number,
          duration: reducedMotion ? 0 : durations.base,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        })
      )
    );
    animation.start();
    return () => animation.stop();
  }, [layoutAnimated, offset, shownWidth, reducedMotion, offsetAnim, widthAnim]);
  const registerDockInset = useUIStore((s) =>
    side === 'left' ? s.registerLeftDockInset : s.registerDockInset
  );
  const unregisterDockInset = useUIStore((s) =>
    side === 'left' ? s.unregisterLeftDockInset : s.unregisterDockInset
  );
  const id = testID ?? DEFAULT_ID;

  // One effect drives both directions: `shown` true animates in (also
  // covers the original mount-is-open case, since slideIn starts off-screen
  // and shown defaults true), `shown` false animates back out and reports
  // through onExited once settled. Never unmount mid-animation — the
  // caller decides when to drop this component, this only tells it when
  // it's safe to. onExited is deliberately left out of the deps: it's a
  // callback, not layout state, and a new closure from the caller must not
  // restart the animation mid-flight.
  useEffect(() => {
    const target = shown ? 0 : side === 'left' ? -distance : distance;
    const duration = reducedMotion ? 0 : shown ? OPEN_MS : durations.quick;
    const animation = Animated.timing(slideIn, {
      toValue: target,
      duration,
      easing: shown ? Easing.out(Easing.cubic) : EXIT_EASING,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      if (finished && !shown) onExited?.();
    });
    return () => animation.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, side, distance, reducedMotion, slideIn]);

  // The animation only reports `finished` when nothing interrupted it. A
  // dependency change mid-exit (a resize moves `distance`), or a frame loop
  // the browser paused, stops it without that flag, and the caller then keeps
  // the leaving panel mounted for good: off-screen, still rendering its rows,
  // whose popovers portal to the body and clamp to the viewport's corner
  // (Oct 14 2026, Opera: every row menu showed twice). So the exit also
  // reports through a timer; useLeavingPanel's onExited is idempotent.
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;
  useEffect(() => {
    if (shown) return;
    const ms = (reducedMotion ? 0 : durations.quick) + 100;
    const timer = setTimeout(() => onExitedRef.current?.(), ms);
    return () => clearTimeout(timer);
  }, [shown, reducedMotion]);

  // Publish this panel's width so floating map chrome (search bar,
  // MapControls, the post hover card's clamp, focusOnBounds padding — see
  // useRightDockInset) stays clear of it without learning about each panel
  // by name. Keyed by id rather than a single flag per surface: when one
  // panel unmounts as another mounts (the dock-arbitration swaps in
  // Map.web.tsx), each effect only ever touches its own key, so the result
  // is correct regardless of which cleanup or setup fires first.
  //
  // shown=false drops the registration immediately rather than waiting for
  // the exit animation or an eventual unmount — the map chrome that reads
  // this inset should move back as soon as the panel starts leaving, not
  // 150ms later. collapsed registers the DOCK_TAB_WIDTH tab's footprint
  // instead of the full panel width.
  useEffect(() => {
    if (!shown) {
      unregisterDockInset(id);
      return;
    }
    registerDockInset(id, collapsed ? DOCK_TAB_WIDTH : width, offset);
    return () => unregisterDockInset(id);
  }, [id, width, offset, collapsed, shown, registerDockInset, unregisterDockInset]);

  const fadeOpacity = slideIn.interpolate({
    inputRange: side === 'left' ? [-distance, 0] : [0, distance],
    outputRange: side === 'left' ? [0, 1] : [1, 0],
    extrapolate: 'clamp',
  });

  // Panels are regions, never dialogs (CLAUDE.md's "Panels, not dialogs"
  // rule) — but only once a caller opts in with a label. Adding the role
  // unconditionally would touch every existing DockedPanel's DOM (and the
  // a11y audit baselines with it) before anything has actually adopted it.
  const hasLabel = Boolean(ariaLabelledBy || accessibilityLabel);
  const regionProps = hasLabel
    ? {
        role: 'region' as const,
        ...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {}),
        ...(accessibilityLabel ? { accessibilityLabel } : {}),
      }
    : {};

  // While the exit plays the panel must not take clicks (it sits over the
  // map) and audits need a DOM signal to wait on. pointerEvents is a prop,
  // not a style, per the animated-fade-freeze lesson; dataSet is the
  // react-native-web hook for data-* attributes (renders data-redi-leaving).
  const leavingProps = (
    shown ? {} : { pointerEvents: 'none', dataSet: { rediLeaving: 'true' } }
  ) as object;

  return (
    <Animated.View
      testID={id}
      {...regionProps}
      {...leavingProps}
      style={[
        styles.panel,
        side === 'left' ? styles.panelLeft : styles.panelRight,
        ELEVATION.lg,
        {
          [side === 'left' ? 'left' : 'right']: layoutAnimated ? offsetAnim : offset,
          width: layoutAnimated ? widthAnim : shownWidth,
          ...(fade ? { opacity: fadeOpacity } : null),
          backgroundColor: colors[backgroundColor],
          // Right dock's border sits on its left (facing edge); left dock's
          // sits on its right — either way, the edge facing the map.
          [side === 'left' ? 'borderRightColor' : 'borderLeftColor']: colors.border,
          transform: [{ translateX: slideIn }],
        },
      ]}>
      {collapsed ? <DockTab side={side} label={collapsedLabel ?? ''} onExpand={onExpand} /> : null}
      <PanelShownContext.Provider value={shown}>
        <View style={collapsed ? styles.hidden : styles.visibleBody}>
          {scroll ? (
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.content}>
              {children}
            </ScrollView>
          ) : (
            <View style={styles.fixedBody}>{children}</View>
          )}
        </View>
      </PanelShownContext.Provider>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  panel: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: Z_INDEX.dockedPanel,
  },
  panelRight: {
    borderLeftWidth: 1,
  },
  panelLeft: {
    borderRightWidth: 1,
  },
  content: {
    flexGrow: 1,
  },
  fixedBody: {
    flex: 1,
    minHeight: 0,
  },
  visibleBody: {
    flex: 1,
    minHeight: 0,
  },
  // Keeps children mounted (so typed form state survives) while the tab is
  // showing instead — display:'none', not unmounting.
  hidden: {
    display: 'none',
  },
});
